/**
 * i18n（国際化）モジュール
 *
 * ブラウザの言語設定に基づいて日本語/英語UIを切り替える
 * - navigator.language が 'ja' で始まる場合は日本語
 * - それ以外は英語
 */

import { createContext, useContext, useMemo } from 'react';
import ja from './ja';
import en from './en';

// 翻訳データ
const translations = { ja, en };

// 言語コンテキスト
const LanguageContext = createContext(null);

/**
 * ブラウザの言語を検出
 * @returns {'ja' | 'en'} 言語コード
 */
export function detectLanguage() {
  if (typeof window === 'undefined') return 'ja';

  const browserLang = navigator.language || navigator.userLanguage || 'ja';
  return browserLang.startsWith('ja') ? 'ja' : 'en';
}

/**
 * 翻訳キーから値を取得（ネストされたキーに対応）
 * @param {Object} translations - 翻訳オブジェクト
 * @param {string} key - ドット区切りのキー（例: 'dashboard.greeting'）
 * @param {Object} params - 置換パラメータ（例: { name: 'John' }）
 * @returns {string} 翻訳された文字列
 */
function getTranslation(translationObj, key, params = {}) {
  // ドット区切りのキーを分解
  const keys = key.split('.');
  let value = translationObj;

  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // キーが見つからない場合はキー自体を返す
      console.warn(`[i18n] Missing translation key: ${key}`);
      return key;
    }
  }

  // 文字列でない場合はキーを返す
  if (typeof value !== 'string') {
    console.warn(`[i18n] Translation value is not a string: ${key}`);
    return key;
  }

  // パラメータを置換（{name} 形式）
  let result = value;
  for (const [param, replacement] of Object.entries(params)) {
    result = result.replace(new RegExp(`\\{${param}\\}`, 'g'), replacement);
  }

  return result;
}

/**
 * 言語プロバイダー
 * アプリのルートで使用
 */
export function LanguageProvider({ children }) {
  const lang = useMemo(() => detectLanguage(), []);
  const t = useMemo(() => translations[lang], [lang]);

  // 翻訳関数
  const translate = useMemo(() => {
    return (key, params = {}) => getTranslation(t, key, params);
  }, [t]);

  const value = useMemo(() => ({
    lang,
    t,
    translate
  }), [lang, t, translate]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * 言語フック
 * @returns {{ lang: string, t: Object, translate: Function }}
 */
export function useLanguage() {
  const context = useContext(LanguageContext);

  // コンテキストがない場合のフォールバック（テスト用）
  if (!context) {
    const lang = detectLanguage();
    const t = translations[lang];
    return {
      lang,
      t,
      translate: (key, params = {}) => getTranslation(t, key, params)
    };
  }

  return context;
}

/**
 * 短縮エイリアス
 * 使用例: const { t } = useT();
 */
export function useT() {
  const { translate } = useLanguage();
  return { t: translate };
}

// デフォルトエクスポート
export default {
  detectLanguage,
  LanguageProvider,
  useLanguage,
  useT,
  translations
};
